import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
interface JustificativasSectionProps {
  scores: Record<string, Record<string, any>>;
  attributes: string[];
  mainBrand: string;
  findCompetitorUrl?: (brand: string) => string;
  getFavicon?: (url: string) => string;
}
const JustificativasSection: React.FC<JustificativasSectionProps> = ({
  scores,
  attributes,
  mainBrand,
  findCompetitorUrl,
  getFavicon
}) => {
  const [selectedAttribute, setSelectedAttribute] = useState<string>(attributes.length > 0 ? attributes[0] : '');
  const [expandedBrands, setExpandedBrands] = useState<Record<string, boolean>>({});
  // Reset expanded brands when attribute changes
  useEffect(() => {
    setExpandedBrands({});
  }, [selectedAttribute]);
  // Check if we have the new format with justifications
  const hasJustificativas = () => {
    for (const brand in scores) {
      const attrValue = scores[brand][selectedAttribute];
      if (typeof attrValue === 'object' && attrValue !== null && 'justificativa' in attrValue) {
        return true;
      }
    }
    return false;
  };
  // Get score value safely (handles both old and new format)
  const getScore = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'object' && value !== null && 'score' in value) {
      return value.score;
    }
    return 0;
  };
  // Get justification safely (only in new format)
  const getJustificativa = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'object' && value !== null && 'justificativa' in value) {
      return value.justificativa;
    }
    return null;
  };
  // Get evidences safely (only in new format)
  const getEvidencias = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'object' && value !== null && 'evidencias' in value && Array.isArray(value.evidencias)) {
      return value.evidencias;
    }
    return [];
  };
  // Toggle brand expansion
  const toggleBrandExpansion = (brand: string) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brand]: !prev[brand]
    }));
  };
  // Sort brands by score for the selected attribute
  const sortedBrands = Object.keys(scores).sort((a, b) => {
    return getScore(b, selectedAttribute) - getScore(a, selectedAttribute);
  });
  return <div className="w-full" data-id="element-750" data-mp="14a5pxxhnfd2y">
      <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-751" data-mp="148ub92mo0kx2">
        Justificativas das Notas
      </h2>
      <p className="text-gray-800 mb-6" data-id="element-752" data-mp="197p6x55qe3yl">
        Explicações detalhadas sobre os critérios utilizados na avaliação e as
        evidências que fundamentam cada nota.
      </p>
      {/* Attribute selector */}
      <div className="mb-6" data-id="element-753" data-mp="3u0sh46gokyp8">
        <label htmlFor="justificativa-attribute-select" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-754" data-mp="3mfood3cjxfy7">
          Selecione um atributo para ver as justificativas:
        </label>
        <div className="relative inline-block w-full max-w-xs" data-id="element-755" data-mp="3vgwm9kb5oerr">
          <select id="justificativa-attribute-select" value={selectedAttribute} onChange={e => setSelectedAttribute(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-[#1649ff] focus:border-[#1649ff] sm:text-sm rounded-md appearance-none shadow-sm text-gray-900" data-id="element-756" data-mp="3vead5g8m9r7f">
            {attributes.map(attribute => <option key={attribute} value={attribute} data-id="element-757" data-mp="37qahc1wg7alz">
                {attribute}
              </option>)}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-758" data-mp="2jzeqb6ztgjfs">
            <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-759" data-mp="2f1v0raxo4k8j" />
          </div>
        </div>
      </div>
      {/* No justifications message */}
      {!hasJustificativas() && <div className="p-4 bg-yellow-50 rounded-md text-yellow-700 mb-6" data-id="element-760" data-mp="2xozxwgqn5du9">
          <p className="font-medium" data-id="element-761" data-mp="32mhz1kyf2orp">Formato Legado</p>
          <p data-id="element-762" data-mp="32lqa2szbrpae">
            Esta análise foi realizada antes de 08/08/2025 e não contém
            justificativas detalhadas. As análises realizadas após esta data
            incluem justificativas e evidências para cada nota.
          </p>
        </div>}
      {/* Justifications section */}
      <div className="space-y-6" data-id="element-763" data-mp="3gclgzzq9jwbc">
        {sortedBrands.map(brand => {
        const score = getScore(brand, selectedAttribute);
        const justificativa = getJustificativa(brand, selectedAttribute);
        const evidencias = getEvidencias(brand, selectedAttribute);
        const isExpanded = !!expandedBrands[brand];
        return <div key={brand} className={`border rounded-lg overflow-hidden ${brand === mainBrand ? 'border-[#1649ff] bg-blue-50' : 'border-gray-200'}`} data-id="element-764" data-mp="1vxt7fuemvzra">
              {/* Brand header with score */}
              <div className={`flex items-center justify-between p-4 cursor-pointer ${brand === mainBrand ? 'bg-blue-100' : 'bg-gray-50'}`} onClick={() => toggleBrandExpansion(brand)} data-id="element-765" data-mp="8bgse2vti72l">
                <div className="flex items-center" data-id="element-766" data-mp="w2chgs8w2o65">
                  {findCompetitorUrl && getFavicon && <img src={getFavicon(findCompetitorUrl(brand))} alt={`${brand} favicon`} className="w-5 h-5 mr-2" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
              }} data-id="element-767" data-mp="s07n1ram9nig" />}
                  <h3 className="font-medium text-gray-900" data-id="element-768" data-mp="349y264jk6cn1">{brand}</h3>
                  <div className="ml-3 px-2 py-1 bg-white rounded text-sm font-bold text-gray-900 border border-gray-200" data-id="element-769" data-mp="34aqyebkw5jqx">
                    {score}
                  </div>
                </div>
                <button className="text-gray-500 hover:text-gray-700" aria-label={isExpanded ? 'Recolher' : 'Expandir'} data-id="element-770" data-mp="2v8zv1ddtpp08">
                  {isExpanded ? <ChevronUp size={20} data-id="element-771" data-mp="2lhdb8snf6hlv" /> : <ChevronDown size={20} data-id="element-772" data-mp="2qaydiww1pxqv" />}
                </button>
              </div>
              {/* Expanded content with justification and evidence */}
              {isExpanded && <div className="p-4 border-t border-gray-200" data-id="element-773" data-mp="2gkwn3yg8038s">
                  {hasJustificativas() ? <>
                      {/* Justificativa section - always shown */}
                      <div className="mb-4" data-id="element-774" data-mp="27j5kx3kw2ejo">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-775" data-mp="1nxn6d4vpc2b0">
                          Justificativa:
                        </h4>
                        {justificativa ? <p className="text-gray-900" data-id="element-776" data-mp="1xw2wb4izt5ix">{justificativa}</p> : <p className="text-gray-500 italic" data-id="element-777" data-mp="1sx9krhn9nole">
                            Não há justificativa disponível para esta marca e
                            atributo.
                          </p>}
                      </div>
                      {/* Evidências section - always shown */}
                      <div data-id="element-778" data-mp="2gmu80cqgd80h">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-779" data-mp="2gm12tg756qkg">
                          Evidências:
                        </h4>
                        {evidencias.length > 0 ? <ul className="list-disc pl-5 space-y-1 text-gray-900" data-id="element-780" data-mp="2pnijazogou9o">
                            {evidencias.map((evidencia, index) => <li key={index} data-id="element-781" data-mp="3s14thivhlew2" data-mpi={index}>{evidencia}</li>)}
                          </ul> : <p className="text-gray-500 italic" data-id="element-782" data-mp="sh8j415rtp1">
                            Não há evidências disponíveis para esta marca e
                            atributo.
                          </p>}
                      </div>
                    </> : <p className="text-gray-500 italic" data-id="element-783" data-mp="3980wbnj28jwp">
                      Não há justificativas detalhadas disponíveis para esta
                      marca e atributo.
                    </p>}
                </div>}
            </div>;
      })}
      </div>
    </div>;
};
export default JustificativasSection;